import { PipelineResponse } from '../../services/caseWorkflowService';

/**
 * Each workflow's column in the pipeline map, keyed by campaign id.
 *
 * The rank is the longest path from a workflow nothing feeds, the same layering
 * the map's left-to-right layout produces, so a list ordered by it reads in the
 * map's order. Older campaigns on a flow share its rank. A campaign the pipeline
 * does not connect has no rank.
 */
export const campaignRanks = (pipeline: PipelineResponse | null): Map<string, number> => {
    const ranks = new Map<string, number>();
    if (!pipeline || !pipeline.edges.length) return ranks;

    const incoming = new Map<string, string[]>();
    for (const edge of pipeline.edges) {
        incoming.set(edge.to, [...(incoming.get(edge.to) ?? []), edge.from]);
    }
    const flowRank = new Map<string, number>();
    const rankOf = (flow: string, seen: Set<string>): number => {
        const known = flowRank.get(flow);
        if (known !== undefined) return known;
        // The contract's flow graph is acyclic (tested where it is derived); the
        // guard only stops a malformed response from recursing forever.
        if (seen.has(flow)) return 0;
        seen.add(flow);
        const sources = incoming.get(flow) ?? [];
        const rank = sources.length ? Math.max(...sources.map((s) => rankOf(s, seen) + 1)) : 0;
        seen.delete(flow);
        flowRank.set(flow, rank);
        return rank;
    };

    for (const node of pipeline.nodes) {
        if (!node.flowCode) continue;
        const rank = rankOf(node.flowCode, new Set());
        ranks.set(node.campaignId, rank);
        for (const older of node.alsoCampaignIds ?? []) ranks.set(older, rank);
    }
    return ranks;
};
